import jsPDF from 'jspdf';
import { COLORS } from './pdfUtils';

/**
 * Gemeinsamer Briefkopf, Briefpapier und Fusszeile fuer alle Geschaeftsbriefe.
 *
 * Herausgeloest aus generatorQuote.ts, damit das Rechnungsmodul dasselbe
 * Briefpapier benutzt statt es zu kopieren. Die Anforderung "gleiches
 * Briefpapier wie Angebote" ist damit konstruktionsbedingt erfuellt: es gibt
 * nur noch eine Implementierung.
 *
 * Der Code ist unveraendert uebernommen. Abgesichert durch
 * generatorQuote.golden.test.ts, das die Zeichenbefehle des Angebots-PDF vor
 * der Extraktion aufgezeichnet hat.
 */

export interface CompanyInfo {
    name?: string;
    address?: string;
    logo?: string;
    footerText?: string;
    /** Schriftgroesse der Fusszeile in pt (Default 7). */
    footerFontSize?: number;
    /**
     * Kleine, rechtsbuendige Kopf-Infospalte auf der Anschreibenseite
     * (Telefon/Mail/USt-IdNr. etc.), zeilenweise durch \n getrennt.
     * Leerzeilen wirken als Gruppentrenner (zusaetzlicher Abstand).
     */
    letterheadText?: string;
    /**
     * Kleine, linksbuendige Kopfzeile unter der oberen Trennlinie
     * (z. B. Verbandsmitgliedschaft V.S.G.K.), darf auf 2 Zeilen umbrechen.
     */
    letterheadLeftLine?: string;
    /** Schriftgroesse der linken Kopfzeile in pt (Default 6.5). */
    letterheadLeftLineFontSize?: number;
    /**
     * Eigene Absenderzeile ueber dem Anschriftfeld (DIN-Kleinzeile). Leer =
     * automatisch "Firmenname • Anschrift". Gilt fuer Angebot UND Rechnung.
     */
    senderLine?: string;
    /**
     * Strukturierte Fusszeile in Spalten (je Spalte ein Zeilen-Array) — die
     * Rechnung setzt hier Firma/Adresse, Kontakt, Pflichtangaben nach
     * § 14 UStG und Bankverbindung aus den strukturierten Feldern, damit das
     * Druckbild nie hinter der E-Rechnung zurueckbleibt. Gesetzt ERSETZT sie
     * den zentrierten Fusszeilentext; ohne Wert aendert sich nichts, und bei
     * Briefpapier entfaellt sie wie die gesamte gezeichnete Fusszeile.
     */
    footerColumns?: string[][];
    /**
     * Index der Fusszeilen-Spalte, die bei Platznot allein gestaucht wird
     * (siehe footerColumnWidths). Die Rechnung zeigt hier auf "Rechtliches" —
     * Firmenname und IBAN in den anderen Spalten brechen dadurch nie um.
     */
    footerFlexColumn?: number;
    /**
     * Firmen-Briefpapier als Ganzseiten-Hintergrund. Ist page1 gesetzt, ersetzt
     * das Briefpapier den selbst gezeichneten Briefkopf/Fusszeile.
     */
    stationery?: {
        page1?: string;
        following?: string;
        topMarginPage1?: number;
        topMarginFollowing?: number;
        bottomMargin?: number;
    };
}

/** Blattmasse in mm. A4 hochkant mit dem Firmenrand. */
export interface PageGeometry {
    pageWidth: number;
    pageHeight: number;
    margin: number;
    contentWidth: number;
    rightX: number;
}

/**
 * Groesste Breite des Logos im Briefkopf in mm.
 *
 * Die Hoehe allein reicht als Schranke nicht: bei einem breiten Logo folgt die
 * Breite dem Seitenverhaeltnis und laeuft ueber den linken Blattrand hinaus.
 * 70 von 170 mm Satzspiegel lassen ein grosszuegiges Logo zu und halten
 * zugleich Abstand zum Anschriftfeld darunter.
 */
export const LOGO_MAX_WIDTH = 70;

/** Hoehe des Logokastens im Briefkopf in mm. */
export const LOGO_HEIGHT = 18;

/**
 * Passt ein Logo unter Wahrung des Seitenverhaeltnisses in den Kasten ein.
 *
 * Der Fehler, den das behebt: frueher war allein die Hoehe fest und die Breite
 * folgte dem Seitenverhaeltnis. Ein breites Logo wurde damit breiter als das
 * Blatt, begann bei einem negativen x und war links abgeschnitten.
 *
 * Liefert null bei unbrauchbaren Massen — sonst waere die Skalierung eine
 * Division durch null und ergaebe NaN-Koordinaten, mit denen jsPDF nichts
 * zeichnet, aber auch nicht meckert.
 */
/**
 * Bildformat einer DataURL fuer jsPDF.
 *
 * jsPDF braucht das Format explizit; ein als "JPEG" deklariertes PNG landet
 * verstuemmelt im Dokument. Modulweit exportiert, damit auch die Bericht-,
 * Leistungsnachweis-, Historien- und Unterlage-Generatoren dieselbe Erkennung
 * benutzen statt hart 'JPEG' zu uebergeben.
 */
export const imgFmt = (dataUrl: string): 'PNG' | 'JPEG' =>
    /^data:image\/png/i.test(dataUrl) ? 'PNG' : 'JPEG';

export const fitLogoBox = (
    natural: { width: number; height: number },
    maxWidth: number = LOGO_MAX_WIDTH,
    maxHeight: number = LOGO_HEIGHT,
): { width: number; height: number } | null => {
    if (!(natural.width > 0) || !(natural.height > 0)) return null;
    const scale = Math.min(maxWidth / natural.width, maxHeight / natural.height);
    return { width: natural.width * scale, height: natural.height * scale };
};

/**
 * Hoehe des gezeichneten Blattfusses in mm — der Streifen, den Inhalt ohne
 * Briefpapier NIE betreten darf.
 *
 * Massgeblich ist die Fusszeile MIT Spalten (drawFooters, Zweig
 * `wrappedColumns`), weil die Rechnung sie benutzt und sie am hoechsten
 * hinaufreicht: die Seitenzahl steht dort auf der Grundlinie pageHeight-23,
 * die Oberkante ihrer Ziffern liegt bei 10 pt rund 2,5 mm darueber, also bei
 * pageHeight-25,5. 28 mm laesst der letzten Inhaltszeile (Grundlinie auf der
 * Schranke, Unterlaengen rund 0,8 mm tiefer) gut 1,5 mm Luft zur Seitenzahl.
 *
 * Vorher stand hier nichts: `createSpaceGuard` mass ohne Briefpapier gegen die
 * BLATTKANTE, und die Fusszeile bekam nur ab, was der Aufrufer zufaellig als
 * `needed` uebergab. Bei den 25 mm des Summenblocks lag die Schranke damit bei
 * pageHeight-25 — mitten in der Seitenzahl.
 *
 * MIT Briefpapier gilt die Konstante NICHT: dort bringt das Briefpapier seine
 * eigene Fusszeile mit, und der gepflegte `bottomMargin` ist das Mass.
 */
export const FOOTER_RESERVE = 28;

/** A4 mit 20 mm Rand — das Format aller Geschaeftsbriefe der Firma. */
export const A4_GEOMETRY: PageGeometry = {
    pageWidth: 210,
    pageHeight: 297,
    margin: 20,
    contentWidth: 170,
    rightX: 190,
};

export interface LetterheadContext {
    /** True, sobald ein Briefpapier hinterlegt ist. Unterdrueckt den eigenen Briefkopf. */
    hasStationery: boolean;
    /** Inhaltsbeginn Seite 1 in mm. */
    topP1: number;
    /** Inhaltsbeginn Folgeseiten in mm. */
    topFollow: number;
    /** Unterer Rand in mm. */
    botMargin: number;
    /** Bildformat einer DataURL — jsPDF braucht das explizit. */
    imgFmt: (dataUrl: string) => 'PNG' | 'JPEG';
    /** Stempelt den Briefpapier-Hintergrund genau einmal pro Seite. */
    stampCurrentBg: () => void;
    /** addPage() + stampCurrentBg() in einem — die einzige erlaubte Art umzublaettern. */
    addPage: () => void;
    /** Inhaltsbeginn einer frischen Folgeseite (mit bzw. ohne Briefpapier). */
    contentTopFollowing: number;
    /**
     * willDrawPage-Callback fuer autoTable. Ohne Briefpapier undefined.
     *
     * Tragend: autoTable legt Seiten SELBST an. Fehlt der Callback, bleibt jede
     * von der Tabelle erzeugte Folgeseite ohne Briefpapier — und das faellt erst
     * beim Kunden auf.
     */
    willDrawPage: (() => void) | undefined;
    /** margin-Objekt fuer autoTable, passend zu den Briefpapier-Raendern. */
    autoTableMargin: () => { left: number; right: number; top?: number; bottom: number };
}

/**
 * Baut den Briefpapier-Kontext fuer ein Dokument.
 *
 * Seite 1 nutzt stationery.page1, alle Folgeseiten stationery.following
 * (Fallback: page1). Der Stempel wird je Seite nur einmal gesetzt (Dedup ueber
 * stampedPages), damit manuelle addPage()-Aufrufe und autoTable-eigene
 * Seitenumbrueche sich nicht gegenseitig doppelt stempeln.
 */
export const createLetterhead = (
    doc: jsPDF,
    company: CompanyInfo,
    geo: PageGeometry = A4_GEOMETRY,
): LetterheadContext => {
    const st = company.stationery;
    const hasStationery = !!st?.page1;
    const topP1 = st?.topMarginPage1 ?? 50;
    const topFollow = st?.topMarginFollowing ?? 25;
    const botMargin = st?.bottomMargin ?? 25;

    const bgFor = (pageNo: number): string | undefined =>
        !hasStationery ? undefined : pageNo === 1 ? st!.page1 : st!.following || st!.page1;

    const stampedPages = new Set<number>();
    const stampCurrentBg = () => {
        if (!hasStationery) return;
        const pageNo = doc.getNumberOfPages();
        if (stampedPages.has(pageNo)) return;
        const img = bgFor(pageNo);
        stampedPages.add(pageNo);
        if (!img) return;
        try {
            doc.addImage(img, imgFmt(img), 0, 0, geo.pageWidth, geo.pageHeight);
        } catch (e) {
            console.warn('PDF: Briefpapier konnte nicht eingebettet werden', e);
        }
    };

    const contentTopFollowing = hasStationery ? topFollow : geo.margin + 5;

    return {
        hasStationery,
        topP1,
        topFollow,
        botMargin,
        imgFmt,
        stampCurrentBg,
        addPage: () => {
            doc.addPage();
            stampCurrentBg();
        },
        contentTopFollowing,
        willDrawPage: hasStationery ? () => stampCurrentBg() : undefined,
        autoTableMargin: () =>
            hasStationery
                ? { left: geo.margin, right: geo.margin, top: topFollow, bottom: botMargin }
                : { left: geo.margin, right: geo.margin, bottom: FOOTER_RESERVE },
    };
};

/**
 * Umbruch-Guard fuer zeilenweise gesetzten Fliesstext.
 *
 * Liest und schreibt die y-Position ueber Callbacks, weil die Generatoren zwei
 * getrennte Laufvariablen fuehren (Anschreiben und Kalkulation) und beide
 * denselben Guard brauchen.
 *
 * `needed` ist der Platz UNTER der Schreibmarke, den der naechste Block
 * mindestens haben will. Beide Zweige klemmen ihn nach unten ab, damit der
 * Blattfuss frei bleibt: mit Briefpapier auf den gepflegten `bottomMargin`,
 * ohne Briefpapier auf FOOTER_RESERVE — dort steht die gezeichnete Fusszeile
 * mit der Seitenzahl.
 */
export const createSpaceGuard = (
    ctx: LetterheadContext,
    geo: PageGeometry,
    getY: () => number,
    setY: (v: number) => void,
) => {
    return (needed: number) => {
        const limit = ctx.hasStationery
            ? geo.pageHeight - Math.max(ctx.botMargin, needed)
            : geo.pageHeight - Math.max(FOOTER_RESERVE, needed);
        if (getY() > limit) {
            ctx.addPage();
            setY(ctx.contentTopFollowing);
        }
    };
};

/**
 * Die einzeilige DIN-5008-Absenderzeile ueber dem Anschriftfeld.
 *
 * Das Anschrift-Feld darf Zeilenumbrueche tragen (die Fusszeile nutzt sie als
 * Zeilentrenner) — hier muessen sie zu Aufzaehlungspunkten werden: jsPDF
 * druckte eine zweite Zeile sonst mitten ueber das Anschriftfeld. Kommas
 * bleiben stehen, damit einzeilige Anschriften unveraendert aussehen.
 */
/**
 * Typografie der linken Kopfzeile: Schriftgroesse in pt und Zeilenabstand in
 * mm. Der Abstand skaliert MIT der Schrift (3 mm bei 6,5 pt) — groessere
 * Schrift mit festem Abstand liefe beim Zweizeilen-Umbruch zusammen.
 * Unbrauchbare Werte fallen auf den bisherigen Standard zurueck.
 */
export const leftLineTypography = (
    fontSize?: number,
): { fontSize: number; lineHeight: number } => {
    const size = Number.isFinite(fontSize) && (fontSize as number) > 0 ? (fontSize as number) : 6.5;
    return { fontSize: size, lineHeight: (size / 6.5) * 3 };
};

export const dinSenderLine = (name?: string, address?: string): string =>
    [name, ...String(address ?? '').split(/\r?\n/)]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' • ');

/**
 * Absenderzeile mit Vorrang fuer die gepflegte Vorgabe (CompanyInfo.senderLine):
 * der Betreiber will dort oft eine andere Schreibweise als in Briefkopf oder
 * Fusszeile. Zeilenumbrueche der Vorgabe werden zu Aufzaehlungspunkten — die
 * DIN-Zeile MUSS einzeilig bleiben (jsPDF druckte eine zweite Zeile mitten
 * uebers Anschriftfeld). Ohne Vorgabe wie bisher Firmenname • Anschrift.
 */
export const resolveSenderLine = (company: {
    name?: string;
    address?: string;
    senderLine?: string;
}): string => {
    const custom = company.senderLine?.trim();
    if (custom) {
        return custom
            .split(/\r?\n/)
            .map((part) => part.trim())
            .filter(Boolean)
            .join(' • ');
    }
    return dinSenderLine(company.name, company.address);
};

export interface Page1HeaderResult {
    /** y-Position, an der das Anschriftfeld beginnt. */
    addressFieldY: number;
    /** Unterkante der linken Kopfzeile. */
    leftBlockBottom: number;
    /** Unterkante der rechten Infospalte — Ort/Datum muss darunter stehen. */
    rightColBottom: number;
}

/**
 * Zeichnet den Briefkopf der Seite 1: Logo, Trennlinie, linke Kopfzeile,
 * rechte Infospalte und die DIN-5008-Absenderzeile ueber dem Anschriftfeld.
 *
 * Bei hinterlegtem Briefpapier entfaellt all das — das Briefpapier traegt den
 * Absender selbst. Der Rueckgabewert sagt dem Aufrufer, wo der Inhalt beginnt.
 */
export const drawPage1Header = (
    doc: jsPDF,
    company: CompanyInfo,
    ctx: LetterheadContext,
    geo: PageGeometry = A4_GEOMETRY,
): Page1HeaderResult => {
    const { margin, rightX } = geo;
    // Briefkopf in EINEM Schwarz (wie der Belegtext): die Grauabstufungen
    // liessen Absenderzeile und Kopfzeile verwaschen wirken. Aliase bleiben,
    // damit die Zeichenstellen unveraendert lesbar sind.
    const [tl0, tl1, tl2] = COLORS.text.primary;
    const [tp0, tp1, tp2] = COLORS.text.primary;
    const [ts0, ts1, ts2] = COLORS.text.primary;
    const [ln0, ln1, ln2] = COLORS.section.line;

    // ── Logo oben rechts (entfaellt bei Briefpapier) ──────────────────────
    const logoTopY = 15;
    const topZoneBottom = logoTopY + LOGO_HEIGHT; // 33mm: unterer Rand des Logokastens
    if (!ctx.hasStationery && company.logo) {
        try {
            const box = fitLogoBox(doc.getImageProperties(company.logo));
            if (box) {
                doc.addImage(
                    company.logo,
                    // Format aus der DataURL, nicht fest "JPEG": ein als JPEG
                    // deklariertes PNG kann jsPDF verstuemmelt einbetten.
                    ctx.imgFmt(company.logo),
                    rightX - box.width,
                    // Unten im Kasten ausrichten: die Trennlinie darunter liegt
                    // fest, ein flaches Logo soll nicht in der Luft haengen.
                    logoTopY + (LOGO_HEIGHT - box.height),
                    box.width,
                    box.height,
                );
            }
        } catch (e) {
            console.warn('PDF: Logo konnte nicht eingebettet werden', e);
        }
    }

    // ── Obere Trennlinie + Kopfzone (entfaellt bei Briefpapier) ───────────
    let leftBlockBottom = topZoneBottom;
    let rightColBottom = topZoneBottom;
    if (!ctx.hasStationery) {
        const ruleY = topZoneBottom + 2; // 35mm
        doc.setDrawColor(ln0, ln1, ln2);
        doc.setLineWidth(0.2);
        doc.line(margin, ruleY, rightX, ruleY);
        const blockTop = ruleY + 3.5;

        // Linke Verbands-Kopfzeile (klein, grau, umbruchsicher innerhalb ~85mm).
        leftBlockBottom = ruleY;
        if (company.letterheadLeftLine?.trim()) {
            const t = leftLineTypography(company.letterheadLeftLineFontSize);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(t.fontSize);
            doc.setTextColor(tl0, tl1, tl2);
            const leftLines = doc.splitTextToSize(company.letterheadLeftLine.trim(), 85);
            let lly = blockTop;
            (leftLines as string[]).forEach((line) => {
                doc.text(line, margin, lly);
                lly += t.lineHeight;
            });
            leftBlockBottom = lly;
        }

        // Rechte Infospalte: erste nicht-leere Zeile groesser (Firmenname, ~11pt),
        // Folgezeilen ~9pt; Mail-/Web-Zeilen kursiv. Leerzeilen = Gruppentrenner.
        if (company.letterheadText?.trim()) {
            const rawLines = company.letterheadText.replace(/\r\n/g, '\n').split('\n');
            let infoY = blockTop;
            let firstDrawn = false;
            doc.setTextColor(tp0, tp1, tp2);
            rawLines.forEach((raw) => {
                const line = raw.trim();
                if (!line) {
                    if (firstDrawn) infoY += 2.2; // Gruppentrenner: nur Abstand
                    return;
                }
                const italic = /^(mail:|www\.)/i.test(line);
                if (!firstDrawn) {
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(11);
                    doc.text(line, rightX, infoY, { align: 'right' });
                    infoY += 5.2;
                    firstDrawn = true;
                    return;
                }
                doc.setFont('helvetica', italic ? 'italic' : 'normal');
                doc.setFontSize(9);
                doc.text(line, rightX, infoY, { align: 'right' });
                infoY += 4.2;
            });
            rightColBottom = infoY;
        }
    }

    // ── Absender-Kleinzeile ueber dem Anschriftfeld (DIN-5008-Stil) ───────
    // Ohne Briefpapier rueckt das Anschriftfeld nach unten, wenn die linke
    // Kopfzeile umbricht (mind. 55mm, damit eine kurze Kopfzeile das Layout haelt).
    const senderLine = resolveSenderLine(company);
    const addressFieldY = ctx.hasStationery ? ctx.topP1 : Math.max(55, leftBlockBottom + 6);
    if (!ctx.hasStationery && senderLine) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(ts0, ts1, ts2);
        doc.text(senderLine, margin, addressFieldY - 2);
        doc.setDrawColor(ln0, ln1, ln2);
        doc.setLineWidth(0.2);
        doc.line(margin, addressFieldY - 1, margin + 85, addressFieldY - 1);
    }

    return { addressFieldY, leftBlockBottom, rightColBottom };
};

/**
 * Breite und Schriftgroesse einer Positionsnummern-Spalte.
 *
 * Eine Positionsnummer ist ein Schluessel, kein Fliesstext. Bricht sie um,
 * liest der Empfaenger eine ANDERE Nummer: aus "1.1.10." wurde in der 10 mm
 * schmalen Pos.-Spalte der Rechnung "1.1.1" mit einem "0." in der Folgezeile.
 * Deshalb waechst die Spalte mit der laengsten vorkommenden Nummer mit.
 *
 * Die Schranken halten den Preis dafuer klein:
 * - `min` ist die bisherige feste Breite. Belege mit kurzen Nummern (1, 2, 3)
 *   drucken damit unveraendert — die Aenderung kostet nur dort Platz, wo sie
 *   gebraucht wird.
 * - `max` ist, was die Leistungsspalte abgeben kann, ohne unleserlich schmal
 *   zu werden.
 * - Reicht selbst `max` nicht, wird die Nummer VERKLEINERT statt umgebrochen
 *   (bis `minFontSize`): klein gedruckt bleibt sie richtig, umgebrochen ist
 *   sie falsch.
 *
 * `measure` misst mit den echten Schriftmetriken des Dokuments bei der
 * Schriftgroesse der Tabelle; Schrift und Groesse stellt der Aufrufer.
 */
export const fitPosColumn = (
    positions: readonly (string | null | undefined)[],
    measure: (text: string) => number,
    o: { min: number; max: number; paddingX: number; fontSize: number; minFontSize?: number },
): { width: number; fontSize: number } => {
    const widest = positions.reduce<number>((w, p) => (p ? Math.max(w, measure(p)) : w), 0);
    const width = Math.min(o.max, Math.max(o.min, widest + 2 * o.paddingX));
    const space = width - 2 * o.paddingX;
    const fontSize =
        widest > space && space > 0
            ? Math.max(o.minFontSize ?? 6, (o.fontSize * space) / widest)
            : o.fontSize;
    return { width, fontSize };
};

/**
 * Spaltenbreiten der Fusszeile aus den natuerlichen Textbreiten.
 *
 * Starre Viertel brachen dort um, wo es am haesslichsten ist: die Firmenspalte
 * trennte "mbH" ab, die Bankspalte die letzten IBAN-Ziffern — waehrend die
 * Kontaktspalte Platz uebrig hatte. Deshalb Wasserstand-Verteilung: passt
 * alles, bekommt jede Spalte ihre natuerliche Breite und der Rest wird
 * gleichmaessig verteilt (kein Umbruch); passt es nicht, behalten schmale
 * Spalten ihre Breite und nur die breiten teilen sich gleichmaessig, was
 * uebrig ist — der Umbruch faellt dann in die Spalten, die ihn am ehesten
 * vertragen.
 */
export const footerColumnWidths = (
    natural: number[],
    available: number,
    /**
     * Spalte, die den Ueberlauf allein schluckt (z. B. "Rechtliches" mit der
     * Geschaeftsfuehrungszeile — die bricht sauber um). Alle anderen behalten
     * dann ihre natuerliche Breite: Firmenname und IBAN brechen NIE. Bleibt
     * der Flex-Spalte zu wenig (<20mm, Einzelwort-Zerfall), greift wieder die
     * Wasserstand-Verteilung.
     */
    flexIndex?: number,
): number[] => {
    const n = natural.length;
    if (!n) return [];
    const total = natural.reduce((a, b) => a + b, 0);
    if (total <= available) {
        const extra = (available - total) / n;
        return natural.map((w) => w + extra);
    }
    if (flexIndex != null && flexIndex >= 0 && flexIndex < n) {
        const others = natural.reduce((a, w, i) => (i === flexIndex ? a : a + w), 0);
        const flexWidth = available - others;
        if (flexWidth >= 20) {
            return natural.map((w, i) => (i === flexIndex ? flexWidth : w));
        }
    }
    // Von schmal nach breit: wer weniger braucht als den fairen Anteil des
    // Restes, bekommt seine Breite; der frei werdende Platz erhoeht den Anteil
    // der verbleibenden breiteren Spalten.
    const bySize = natural.map((w, i) => ({ w, i })).sort((a, b) => a.w - b.w);
    const widths = new Array<number>(n).fill(0);
    let remaining = available;
    let remainingCols = n;
    for (const { w, i } of bySize) {
        const give = Math.min(w, remaining / remainingCols);
        widths[i] = give;
        remaining -= give;
        remainingCols -= 1;
    }
    return widths;
};

/**
 * Fusszeile auf allen Seiten. Als letzter Schritt aufzurufen, weil erst dann
 * die Gesamtseitenzahl feststeht.
 *
 * Mit Briefpapier: nur eine kleine Seitenzahl im unteren Rand, weil das
 * Briefpapier seine eigene Fusszeile mitbringt.
 * Ohne Briefpapier: Seitenzahl "1/3" rechts oberhalb einer Trennlinie,
 * darunter der zentrierte Fusszeilentext. Ab Seite 2 zusaetzlich der
 * Firmenname oben rechts.
 */
export const drawFooters = (
    doc: jsPDF,
    company: CompanyInfo,
    ctx: LetterheadContext,
    geo: PageGeometry = A4_GEOMETRY,
): void => {
    const { pageWidth, pageHeight, margin, rightX } = geo;
    // Fusszeile im selben Schwarz wie der Belegtext — ein Beleg, eine Farbe.
    const [tp0, tp1, tp2] = COLORS.text.primary;
    const [ts0, ts1, ts2] = COLORS.text.primary;
    const [tl0, tl1, tl2] = COLORS.text.primary;
    const [ln0, ln1, ln2] = COLORS.section.line;

    const pageCount = doc.getNumberOfPages();
    // ?? statt ||: eine bewusst geleerte Fusszeile ('') muss leer bleiben
    // duerfen. Nur ein nie gepflegtes Feld (undefined/null) faellt auf den
    // Firmennamen zurueck — dieselbe Drei-Zustaende-Regel wie
    // services/profileSettings.resolveFooterText.
    const footerText = (company.footerText ?? company.name ?? '').trim();

    // Spalten-Fusszeile: Zeilen je Spalte auf die Spaltenbreite umbrechen.
    // Einmal VOR der Seitenschleife berechnet — der Umbruch haengt nur von der
    // Schriftgroesse ab, nicht von der Seite.
    const fs = company.footerFontSize ?? 7;
    const columns = (company.footerColumns ?? [])
        .map((col) => col.map((l) => l?.trim()).filter(Boolean) as string[])
        .filter((col) => col.length > 0);
    let wrappedColumns: string[][] = [];
    let colX: number[] = [];
    let colLineH = 3.3;
    if (columns.length) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fs);
        // Breite je Spalte nach Inhalt statt starrer Viertel — sonst bricht
        // die Firmenspalte "mbH" und die Bankspalte die IBAN-Endziffern um,
        // waehrend daneben Platz frei bleibt.
        const gap = 4;
        const available = rightX - margin - gap * (columns.length - 1);
        const natural = columns.map((col) =>
            Math.max(...col.map((line) => doc.getTextWidth(line))),
        );
        const widths = footerColumnWidths(natural, available, company.footerFlexColumn);
        colX = [];
        let x = margin;
        widths.forEach((w) => {
            colX.push(x);
            x += w + gap;
        });
        wrappedColumns = columns.map((col, i) =>
            // Hauchduenner Zuschlag: bei exakt natuerlicher Breite darf die
            // Messrundung von splitTextToSize keinen Umbruch ausloesen.
            col.flatMap((line) => doc.splitTextToSize(line, widths[i] + 0.1) as string[]),
        );
        const maxLines = Math.max(...wrappedColumns.map((c) => c.length));
        // Block beginnt fest bei -18 mm; laeuft eine Spalte durch Umbrueche
        // laenger, wird der Zeilenabstand gestaucht statt unter den Blattrand
        // zu schreiben.
        if (maxLines > 1) colLineH = Math.min(3.3, (pageHeight - 5 - (pageHeight - 18)) / (maxLines - 1));
    }

    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        if (i >= 2 && !ctx.hasStationery && company.name) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(ts0, ts1, ts2);
            doc.text(company.name, rightX, 12, { align: 'right' });
        }
        if (ctx.hasStationery) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);
            doc.setTextColor(tl0, tl1, tl2);
            const pnY = pageHeight - Math.max(ctx.botMargin - 5, 4);
            doc.text(`Seite ${i} von ${pageCount}`, rightX, pnY, { align: 'right' });
            continue;
        }
        if (wrappedColumns.length) {
            // Spalten-Fusszeile (Muster-Layout): Seitenzahl rechts ueber der
            // Trennlinie, darunter die Spaltenbloecke linksbuendig.
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(tp0, tp1, tp2);
            doc.text(`${i}/${pageCount}`, rightX, pageHeight - 23, { align: 'right' });
            doc.setDrawColor(ln0, ln1, ln2);
            doc.setLineWidth(0.1);
            doc.line(margin, pageHeight - 21, rightX, pageHeight - 21);
            doc.setFontSize(fs);
            doc.setTextColor(ts0, ts1, ts2);
            wrappedColumns.forEach((col, c) => {
                col.forEach((line, l) => {
                    doc.text(line, colX[c], pageHeight - 18 + l * colLineH);
                });
            });
            continue;
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(tp0, tp1, tp2);
        doc.text(`${i}/${pageCount}`, rightX, pageHeight - 20, { align: 'right' });
        doc.setDrawColor(ln0, ln1, ln2);
        doc.setLineWidth(0.1);
        doc.line(margin, pageHeight - 15, rightX, pageHeight - 15);
        if (footerText) {
            doc.setFontSize(company.footerFontSize ?? 7);
            doc.setTextColor(ts0, ts1, ts2);
            doc.text(footerText.replace(/\s*\n\s*/g, ' '), pageWidth / 2, pageHeight - 10, {
                align: 'center',
            });
        }
    }
};

// ── Formatierung ──────────────────────────────────────────────────────────

/** de-DE-Zahl mit 2 Nachkommastellen (Tausenderpunkt, Dezimalkomma). */
export const fmtNum = (n: number): string =>
    (Number.isFinite(n) ? n : 0).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

/** "1.234,56 €". */
export const fmtEur = (n: number): string => `${fmtNum(n)} €`;

/** Langes deutsches Datum, z.B. "24. Juli 2026". */
export const longDate = (dateStr?: string): string =>
    new Date(dateStr || Date.now()).toLocaleDateString('de-DE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

/**
 * Ort aus der Firmenadresse heuristisch ableiten: Text nach der 5-stelligen PLZ.
 * "Musterstr. 1, 12345 Musterstadt" -> "Musterstadt". Kein Treffer -> undefined.
 */
export const cityFromAddress = (address?: string): string | undefined => {
    if (!address) return undefined;
    const m = address.match(/\b\d{5}\s+(.+?)\s*$/);
    return m ? m[1].trim() : undefined;
};
